import { forwardRef, type SelectHTMLAttributes } from 'react';
import { cx } from '../cx';
import { Field, fieldStyles as styles, type FieldProps } from '../Field/Field';
import { Icon } from '../Icon';

// Select — DESIGN_SYSTEM §12 row 4 (`.select`): the native <select>, styled, with the chevrons glyph. The
// custom listbox that follows `.menu` arrives with the Menu primitive (T-035); the native control is
// keyboard-complete and screen-reader-complete on its own.

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps
  extends
    Omit<FieldProps, 'children' | 'id' | 'className'>,
    Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  id?: string;
  /** Options as data; or pass <option> children. */
  options?: readonly SelectOption[];
  className?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, optional, hint, error, testID, id, options, className, children, ...rest },
  ref,
) {
  return (
    <Field
      label={label}
      optional={optional}
      hint={hint}
      error={error}
      id={id}
      className={className}
    >
      {(control) => (
        <span className={styles.select}>
          <select
            ref={ref}
            className={cx(styles.box, error ? styles.invalid : undefined)}
            data-testid={testID}
            {...control}
            {...rest}
          >
            {options
              ? options.map((option) => (
                  <option key={option.value} value={option.value} disabled={option.disabled}>
                    {option.label}
                  </option>
                ))
              : children}
          </select>
          <Icon name="chevrons-up-down" className={styles.selectIcon} />
        </span>
      )}
    </Field>
  );
});
